import { useEffect, useRef, useState, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { api, unwrap, type ImportCounts } from "../api/client";
import { goTo } from "../nav";
import { suggestFrom } from "../admin/ProfileGenerator";
import { ItemRow, ItemsHead, size } from "./items";
import { useSession } from "../auth/session";
import { CardImport } from "./CardImport";
import { UsbImport } from "./UsbImport";
import { fromDrop, fromInput, type Picked } from "./picked";
import { cancelUpload } from "./upload";
import { cancel, retry, setOpened, upload, useTransfers } from "./transfers";

/** Recordings by extension; anything else is rejected on arrival anyway. */
const RECORDING =
  /\.(mp4|mov|m4v|mkv|webm|avi|m4a|wav|opus|ogg|flac|mp3|gpx|kml|nmea|geojson|csv)$/i;

export function ImportPage() {
  const qc = useQueryClient();
  const volumes = useSession().data?.volumes ?? false;
  const libraries = useQuery({
    queryKey: ["imports", "libraries"],
    queryFn: () => unwrap(api.GET("/api/imports/libraries")),
  });
  const [chosen, setChosen] = useState<string | null>(null);
  const { opened, transfers, finished } = useTransfers();
  const [closed, setClosed] = useState<ImportCounts | null>(null);
  const [dragging, setDragging] = useState(false);
  /** Files found so far while reading a dropped folder. */
  const [reading, setReading] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);

  // An import left open, e.g. before a page reload, carries on.
  const openImports = useQuery({
    queryKey: ["imports", "open"],
    queryFn: () => unwrap(api.GET("/api/imports/open")),
  });
  const restored = openImports.data?.[0];
  const batch =
    opened ??
    (restored ? { id: restored.id, libraryId: restored.library_id } : null);

  const libraryId =
    batch?.libraryId ?? chosen ?? libraries.data?.[0]?.library_id ?? null;
  const path = { params: { path: { id: batch?.id ?? "" } } };
  const items = useQuery({
    queryKey: ["imports", batch?.id, "items"],
    enabled: batch !== null,
    queryFn: () => unwrap(api.GET("/api/imports/{id}/items", path)),
  });
  const pending = useQuery({
    queryKey: ["imports", batch?.id, "uploads"],
    enabled: batch !== null,
    queryFn: () => unwrap(api.GET("/api/imports/{id}/uploads", path)),
  });
  const refresh = (batchId: string) => {
    void qc.invalidateQueries({ queryKey: ["imports", batchId] });
  };
  useEffect(() => {
    if (finished) void qc.invalidateQueries({ queryKey: ["imports"] });
  }, [finished, qc]);
  const inFlight = transfers.filter((t) => t.batchId === batch?.id);

  const open = useMutation({
    mutationFn: ({ files, library }: { files: Picked[]; library: string }) =>
      upload(files, batch, library, (file) => {
        const unfinished = pending.data?.find(
          (p) => p.filename === file.name && p.length === file.size,
        );
        return unfinished && `/api/uploads/${unfinished.id}`;
      }),
  });
  const close = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST("/api/imports/{id}/close", { params: { path: { id } } })),
    onSuccess: (counts) => {
      setClosed(counts);
      setOpened(null);
      qc.setQueryData(["imports", "open"], []);
      void qc.invalidateQueries({ queryKey: ["imports", "open"] });
    },
  });

  /** Files waiting while the user decides about names no profile matches. */
  const [unmatched, setUnmatched] = useState<{
    files: Picked[];
    names: string[];
  } | null>(null);

  const send = (files: Picked[]) => {
    if (!libraryId) return;
    setUnmatched(null);
    setClosed(null);
    open.mutate({ files, library: libraryId });
  };

  // Recordings whose names no profile matches would all land in review: say so first.
  const add = (files: Picked[]) => {
    if (!files.length || !libraryId) return;
    const names = files
      .filter(({ file }) => RECORDING.test(file.name))
      .slice(0, 1000)
      .map(({ file, folder }) => (folder ? `${folder}/${file.name}` : file.name));
    if (!names.length) {
      send(files);
      return;
    }
    void api
      .POST("/api/profiles/test", { body: { profile: null, filenames: names } })
      .then(({ data }) => {
        const misses = (data ?? [])
          .filter((m) => !m.profile)
          .map((m) => m.filename);
        if (misses.length) setUnmatched({ files, names: misses });
        else send(files);
      })
      .catch(() => send(files));
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    // fromDrop reads the dropped items synchronously, before the event ends.
    setReading(0);
    void fromDrop(e.dataTransfer, setReading)
      .then(add)
      .finally(() => setReading(null));
  };

  if (libraries.isError)
    return <Alert severity="error">{libraries.error.message}</Alert>;
  const noLibrary = libraries.isSuccess && libraries.data.length === 0;
  const uploading = inFlight.some((r) => !r.error);
  const sentBytes = inFlight.reduce((n, r) => n + r.sent, 0);
  const allBytes = inFlight.reduce((n, r) => n + r.size, 0);
  const left = inFlight.filter((r) => !r.error).length;
  const interrupted = (pending.data ?? []).filter(
    (p) => !inFlight.some((r) => r.name === p.filename && r.size === p.length),
  );

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Import
      </Typography>

      {noLibrary && (
        <Alert severity="info">
          You aren't in a team that can import. Ask an administrator to add you
          to one.
        </Alert>
      )}
      {open.isError && <Alert severity="error">{open.error.message}</Alert>}
      {close.isError && <Alert severity="error">{close.error.message}</Alert>}
      {closed && (
        <Alert
          severity="success"
          action={
            closed.needs_review > 0 && (
              <Button
                color="inherit"
                size="small"
                onClick={() => goTo("/needs-review")}
              >
                Review
              </Button>
            )
          }
        >
          Import closed: {closed.catalogued} imported, {closed.needs_review}{" "}
          need review, {closed.duplicates} already held, {closed.rejected}{" "}
          rejected.
        </Alert>
      )}

      {libraries.isSuccess &&
        !noLibrary &&
        (volumes ? (
          <UsbImport libraryId={libraryId} />
        ) : (
          <CardImport libraryId={libraryId} />
        ))}

      {libraries.isSuccess && !noLibrary && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" component="h3">
              Upload files
            </Typography>
            {(libraries.data?.length ?? 0) > 1 && (
              <FormControl sx={{ maxWidth: 320 }} disabled={batch !== null}>
                <InputLabel id="import-library">Team library</InputLabel>
                <Select
                  labelId="import-library"
                  label="Team library"
                  value={libraryId ?? ""}
                  onChange={(e) => setChosen(e.target.value)}
                >
                  {libraries.data?.map((l) => (
                    <MenuItem key={l.library_id} value={l.library_id}>
                      {l.team_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: dragging ? "primary.main" : "divider",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                bgcolor: dragging ? "action.hover" : undefined,
              }}
            >
              <Typography color="text.secondary">
                Drop recordings or a unit's folder here, or{" "}
                <Button
                  onClick={() => input.current?.click()}
                  disabled={!libraryId || open.isPending}
                >
                  choose files
                </Button>{" "}
                or{" "}
                <Button
                  onClick={() => folderInput.current?.click()}
                  disabled={!libraryId || open.isPending}
                >
                  choose a folder
                </Button>
              </Typography>
              <input
                ref={input}
                type="file"
                multiple
                hidden
                aria-label="Files to import"
                onChange={(e) => {
                  // fromInput copies the list; clearing the input empties it.
                  add(fromInput(e.target.files));
                  e.target.value = "";
                }}
              />
              <input
                ref={(el) => {
                  folderInput.current = el;
                  el?.setAttribute("webkitdirectory", "");
                }}
                type="file"
                hidden
                aria-label="Folder to import"
                onChange={(e) => {
                  add(fromInput(e.target.files));
                  e.target.value = "";
                }}
              />
            </Box>
            {unmatched && (
              <Alert
                severity="warning"
                action={
                  <Stack direction="row" sx={{ gap: 1 }}>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        suggestFrom(unmatched.names.slice(0, 200));
                        setUnmatched(null);
                        goTo("/profiles");
                      }}
                    >
                      Make a profile
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => send(unmatched.files)}
                    >
                      Upload anyway
                    </Button>
                  </Stack>
                }
              >
                {unmatched.names.length} of {unmatched.files.length} file
                names match no filename profile, so they would need review one
                by one. A profile made from these names identifies them all.
              </Alert>
            )}
            {(reading !== null || (open.isPending && !batch)) && (
              <Stack spacing={1} aria-label="Preparing the upload">
                <Typography variant="body2">
                  {reading !== null
                    ? `Reading the folder: ${reading} files found…`
                    : "Opening the import…"}
                </Typography>
                <LinearProgress />
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary">
              The folder you choose or drop names the unit that recorded it (for
              example Van7); some devices need it to tell their units apart.
              Uploads resume after a network drop. Each file is hashed on
              arrival and checked in a sandbox.
            </Typography>
          </Stack>
        </Paper>
      )}

      {batch && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 2, flexWrap: "wrap" }}
            >
              <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
                This import
              </Typography>
              {interrupted.length === 0 && !uploading && (
                <Typography variant="body2" color="text.secondary">
                  Newest first. Close the import when this card or folder is
                  done; the next starts a fresh list.
                </Typography>
              )}
              {interrupted.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Resume or cancel interrupted uploads before closing.
                </Typography>
              )}
              <Button
                variant="outlined"
                disabled={
                  uploading || interrupted.length > 0 || close.isPending
                }
                onClick={() => close.mutate(batch.id)}
              >
                Close import
              </Button>
            </Stack>
            {inFlight.length > 0 && (
              <Stack spacing={1} aria-label="Upload progress">
                <Typography variant="body2">
                  {left} file{left === 1 ? "" : "s"} to go: {size(sentBytes)}{" "}
                  of {size(allBytes)} sent. Keep this page open; you can work
                  elsewhere in AVGV meanwhile.
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={allBytes ? (100 * sentBytes) / allBytes : 0}
                />
              </Stack>
            )}
            <TableContainer>
              <Table
                size="small"
                sx={{ minWidth: 860, "& .nowrap": { whiteSpace: "nowrap" } }}
              >
                <ItemsHead />
                <TableBody>
                  {interrupted.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell
                        className="nowrap"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {p.filename}
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          Interrupted at{" "}
                          {p.length
                            ? Math.floor((100 * p.offset) / p.length)
                            : 0}
                          %. Choose the file again to resume.
                        </Typography>
                      </TableCell>
                      <TableCell className="nowrap" align="right">
                        {size(p.length)}
                      </TableCell>
                      <TableCell />
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => {
                            void cancelUpload(p.id).then(() =>
                              refresh(batch.id),
                            );
                          }}
                        >
                          Cancel
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {inFlight.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell
                        className="nowrap"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {r.folder ? `${r.folder}/` : ""}
                        {r.name}
                      </TableCell>
                      <TableCell colSpan={3}>
                        {r.error ? (
                          <Typography color="error" variant="body2">
                            {r.error}
                          </Typography>
                        ) : r.waiting ? (
                          <Typography variant="body2" color="text.secondary">
                            Waiting
                          </Typography>
                        ) : (
                          <LinearProgress
                            variant="determinate"
                            value={r.size ? (100 * r.sent) / r.size : 0}
                            aria-label={`Uploading ${r.name}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="nowrap" align="right">
                        {size(r.size)}
                      </TableCell>
                      <TableCell />
                      <TableCell>
                        <Stack
                          direction="row"
                          sx={{ gap: 1, alignItems: "center" }}
                        >
                          {r.error ? (
                            <Button size="small" onClick={() => retry(r.key)}>
                              Retry
                            </Button>
                          ) : (
                            <Chip
                              size="small"
                              label={
                                r.waiting
                                  ? "Waiting"
                                  : r.sent < r.size
                                    ? "Uploading"
                                    : "Checking"
                              }
                            />
                          )}
                          {r.sent < r.size && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => void cancel(r.key)}
                            >
                              Cancel
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {[...(items.data ?? [])].reverse().map((i, n) => (
                    <ItemRow key={`${i.sha256}:${n}`} item={i} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
